import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (error) {
      if (this.props.fallback) return this.props.fallback(error, this.reset);
      return (
        <div className="flex flex-col items-center justify-center h-full min-h-[300px] gap-4 p-8 text-center">
          <div className="text-red-400 text-sm font-mono bg-background-secondary border border-red-800 rounded-lg p-4 max-w-xl w-full text-left">
            <div className="font-semibold mb-2 text-red-300">Page render error</div>
            <div className="text-red-400 text-xs break-all">{error.message}</div>
          </div>
          <button
            onClick={this.reset}
            className="px-4 py-2 text-sm bg-background-elevated hover:bg-background-elevated text-text-primary rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
